import { lazy } from 'react';
import { RouteObject, Navigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth } from '../contexts/AuthContext';

// Lazy load pages
const HomePage = lazy(() => import('../pages/home/page'));
const LoginPage = lazy(() => import('../pages/auth/LoginPage'));
const SignupPage = lazy(() => import('../pages/auth/SignupPage'));
const OnboardingPage = lazy(() => import('../pages/onboarding/OnboardingPage'));
const DashboardPage = lazy(() => import('../pages/dashboard/DashboardPage'));
const MealPlanPage = lazy(() => import('../pages/meal-plan/MealPlanPage'));
const WorkoutPlanPage = lazy(() => import('../pages/workout-plan/WorkoutPlanPage'));
const DailyLogPage = lazy(() => import('../pages/log/DailyLogPage'));
const VitalsPage = lazy(() => import('../pages/vitals/VitalsPage'));
const ImportTestPage = lazy(() => import('../pages/import-tests/ImportTestPage'));
const NotFound = lazy(() => import('../pages/NotFound'));

// Demo pages
const DemoDashboardPage = lazy(() => import('../pages/demo/DemoDashboardPage'));

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Public Route Component (redirect to daily log if logged in)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-orange-500 border-t-transparent"></div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/log" replace />;
  }

  return <>{children}</>;
}

const routes: RouteObject[] = [
  {
    path: '/',
    element: <PublicRoute><HomePage /></PublicRoute>,
  },
  {
    path: '/login',
    element: <PublicRoute><LoginPage /></PublicRoute>,
  },
  {
    path: '/signup',
    element: <PublicRoute><SignupPage /></PublicRoute>,
  },
  {
    path: '/onboarding',
    element: <ProtectedRoute><OnboardingPage /></ProtectedRoute>,
  },
  {
    path: '/_import-tests',
    element: <ProtectedRoute><ImportTestPage /></ProtectedRoute>,
  },
  // Demo routes (no authentication required)
  {
    path: '/demo',
    element: <DashboardLayout />,
    children: [
      {
        path: '',
        element: <DemoDashboardPage />,
      },
      {
        path: 'dashboard',
        element: <DemoDashboardPage />,
      },
      {
        path: 'meal-plan',
        element: <MealPlanPage />,
      },
      {
        path: 'workout-plan',
        element: <WorkoutPlanPage />,
      },
      {
        path: 'log',
        element: <DailyLogPage />,
      },
      {
        path: 'vitals',
        element: <VitalsPage />,
      },
    ],
  },
  {
    path: '/',
    element: <ProtectedRoute><DashboardLayout /></ProtectedRoute>,
    children: [
      {
        path: 'dashboard',
        element: <DashboardPage />,
      },
      {
        path: 'meal-plan',
        element: <MealPlanPage />,
      },
      {
        path: 'workout-plan',
        element: <WorkoutPlanPage />,
      },
      {
        path: 'log',
        element: <DailyLogPage />,
      },
      {
        path: 'vitals',
        element: <VitalsPage />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFound />,
  },
];

export default routes;
